import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { catchError, exhaustMap, map, Observable, of, tap } from 'rxjs';
import { handleError } from '../../utils/handle-error';
import { HttpClient } from '@angular/common/http';
import { ComponentStore } from '@ngrx/component-store';
import { TMenuItem } from '../../x-kit/components/x-menu/x-menu.component';

export type SearchDictState = {
  kabupaten: TMenuItem[];
  desa: TMenuItem[];
  ownershipType: TMenuItem[];
};

@Injectable({
  providedIn: 'root',
})
export class SearchDictsService extends ComponentStore<SearchDictState> {
  constructor(private http: HttpClient) {
    super({
      kabupaten: [],
      desa: [],
      ownershipType: [],
    });
  }

  public kabupatenList$: Observable<TMenuItem[]> = of(null).pipe(
    map((it) => this.get().kabupaten),
    exhaustMap((it) => {
      if (it.length) {
        return of(it);
      } else {
        return this.getKabupatenList('');
      }
    }),
  );

  public ownershipList$: Observable<TMenuItem[]> = of(null).pipe(
    map((it) => this.get().ownershipType),
    exhaustMap((it) => {
      if (it.length) {
        return of(it);
      } else {
        return this.getOwnershipTypeList();
      }
    }),
  );

  public getKabupatenList(term = ''): Observable<TMenuItem[]> {
    const body = term !== '' ? { subSearch: term } : {};

    if (!term && this.get().kabupaten.length) {
      return of(this.get().kabupaten);
    }
    return this.http
      .post<{ id: number; name: string }[]>(`${environment.apiUrl}/api/search/kabupaten/list`, body)
      .pipe(
        map((data) => {
          return data.map((it) => {
            return {
              id: it.id.toString(),
              text: it.name,
            };
          });
        }),
        tap((data) => {
          if (term === '') {
            this.patchState({ kabupaten: data });
          }
        }),
        catchError(err => {
          handleError(err, { source: 'search-dicts', list: 'dict' });
          return of([] as TMenuItem[]);
        }),
      );
  }

  public getDesaList(parentKabupatenId: string, term = ''): Observable<TMenuItem[]> {
    const body =
      term !== ''
        ? { subSearch: term, parentCode: parentKabupatenId }
        : { parentCode: parentKabupatenId };

    return this.http
      .post<{ id: number; name: string }[]>(`${environment.apiUrl}/api/search/desa/list`, body)
      .pipe(
        map((data) => {
          return data.map((it) => {
            return {
              id: it.id.toString(),
              text: it.name,
            };
          });
        }),
        catchError(err => {
          handleError(err, { source: 'search-dicts', list: 'dict' });
          return of([] as TMenuItem[]);
        }),
      );
  }

  private getOwnershipTypeList(): Observable<TMenuItem[]> {
    return this.http
      .post<{ [name: number]: string }>(`${environment.apiUrl}/api/search/ownershiptype/list`, {})
      .pipe(
        map((data) => {
          return Object.values(data).map((it, index) => {
            return {
              id: index.toString(),
              text: it,
            };
          });
        }),
        tap((data) => {
          this.patchState({ ownershipType: data });
        }),
        catchError(err => {
          handleError(err, { source: 'search-dicts', list: 'ownership' });
          return of([]);
        }),
      );
  }
}
